/**
 * Results Filter Bar - Filter controls for the Results Explorer
 */

export type GroupBy = 'none' | 'prompt' | 'config' | 'date' | 'run';

interface FilterSelectProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  children: React.ReactNode;
}

function FilterSelect({ label, value, onChange, children }: FilterSelectProps) {
  return (
    <div>
      <label
        className="block text-sm font-medium mb-2"
        style={{ color: '#1A1A1A' }}
      >
        {label}
      </label>
      <select
        value={value}
        onChange={e => onChange(e.target.value)}
        className="w-full p-3 rounded-lg text-sm"
        style={{
          border: '1px solid #E8E0D8',
          background: 'white',
          color: '#1A1A1A',
        }}
      >
        {children}
      </select>
    </div>
  );
}

interface ResultsFilterBarProps {
  // Filter values
  groupBy: GroupBy;
  runFilter: string;
  promptFilter: string;
  configFilter: string;
  enabledFilter: string;
  searchText: string;

  // Filter setters
  onGroupByChange: (value: GroupBy) => void;
  onRunFilterChange: (value: string) => void;
  onPromptFilterChange: (value: string) => void;
  onConfigFilterChange: (value: string) => void;
  onEnabledFilterChange: (value: string) => void;
  onSearchChange: (value: string) => void;
  onSearchSubmit: () => void;

  // Data for filter options
  runs: any[];
  prompts: any[];
  configs: any[];
}

export function ResultsFilterBar({
  groupBy,
  runFilter,
  promptFilter,
  configFilter,
  enabledFilter,
  searchText,
  onGroupByChange,
  onRunFilterChange,
  onPromptFilterChange,
  onConfigFilterChange,
  onEnabledFilterChange,
  onSearchChange,
  onSearchSubmit,
  runs,
  prompts,
  configs,
}: ResultsFilterBarProps) {
  return (
    <div
      className="bg-white rounded-xl p-6 mb-8"
      style={{ border: '1px solid #E8E0D8' }}
    >
      <div className="grid gap-6">
        {/* Row 1: Group By, Eval Run, Config Status */}
        <div className="grid grid-cols-3 gap-4">
          <FilterSelect
            label="Group By"
            value={groupBy}
            onChange={v => onGroupByChange(v as GroupBy)}
          >
            <option value="none">None (Flat List)</option>
            <option value="run">Eval Run</option>
            <option value="prompt">Prompt</option>
            <option value="config">Model Config</option>
            <option value="date">Date</option>
          </FilterSelect>

          <FilterSelect
            label="Eval Run"
            value={runFilter}
            onChange={onRunFilterChange}
          >
            <option value="all">All Runs</option>
            {runs.map(r => (
              <option key={r.id} value={r.id}>
                {r.name}
              </option>
            ))}
          </FilterSelect>

          <FilterSelect
            label="Config Status"
            value={enabledFilter}
            onChange={onEnabledFilterChange}
          >
            <option value="all">All Configs</option>
            <option value="enabled">Enabled Only</option>
            <option value="disabled">Disabled Only</option>
          </FilterSelect>
        </div>

        {/* Row 2: Prompt & Config Filters */}
        <div className="grid grid-cols-2 gap-4">
          <FilterSelect
            label="Prompt"
            value={promptFilter}
            onChange={onPromptFilterChange}
          >
            <option value="all">All Prompts</option>
            {prompts.map(p => (
              <option key={p.id} value={p.id}>
                {p.name || p.text?.slice(0, 50) || 'Unnamed Prompt'}
              </option>
            ))}
          </FilterSelect>

          <FilterSelect
            label="Model Config"
            value={configFilter}
            onChange={onConfigFilterChange}
          >
            <option value="all">All Configs</option>
            {configs.map(c => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </FilterSelect>
        </div>

        {/* Row 3: Search */}
        <div>
          <label
            className="block text-sm font-medium mb-2"
            style={{ color: '#1A1A1A' }}
          >
            Search
          </label>
          <input
            type="text"
            value={searchText}
            onChange={e => onSearchChange(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                onSearchSubmit();
              }
            }}
            placeholder="Search in prompts or outputs..."
            className="w-full p-3 rounded-lg text-sm"
            style={{
              border: '1px solid #E8E0D8',
              color: '#1A1A1A',
            }}
          />
        </div>
      </div>
    </div>
  );
}
